(() => {
  const ROOT_SELECTOR = '[data-hormonize-showcase-sync]';
  const CACHE_PREFIX = 'hormonizeShowcaseSectionId:';

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const stripAssets = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html;
    template.content.querySelectorAll('script, link[rel="stylesheet"]').forEach((node) => {
      node.remove();
    });
    return template.content;
  };

  const announceReady = (root) => {
    root.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        bubbles: true,
      })
    );
  };

  const getRootPath = () => window.Shopify?.routes?.root || '/';

  const getThemeId = () => {
    const fromShopify = window.Shopify?.theme?.id;
    if (fromShopify) return String(fromShopify);

    const fromQuery = new URLSearchParams(window.location.search).get('preview_theme_id');
    return fromQuery ? String(fromQuery) : '';
  };

  const buildUrl = (params = {}) => {
    const url = new URL(getRootPath(), window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    });
    return url.toString();
  };

  const fetchJson = async (url) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Request failed (${response.status})`);
    }

    return response.json();
  };

  const resolveSectionId = async (configuredId) => {
    const shortId = (configuredId || '').trim();
    if (!shortId) return '';

    if (shortId.includes('__')) {
      return shortId;
    }

    const themeId = getThemeId();
    const cacheKey = `${CACHE_PREFIX}${themeId}:${shortId}`;

    try {
      const cached = window.sessionStorage?.getItem(cacheKey);
      if (cached) return cached;
    } catch (_error) {
      /* sessionStorage unavailable */
    }

    const homepageUrl = buildUrl({
      preview_theme_id: themeId,
    });

    const response = await fetch(homepageUrl, {
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
      },
    });

    if (!response.ok) {
      throw new Error(`Homepage request failed (${response.status})`);
    }

    const html = await response.text();
    const pattern = new RegExp(
      `shopify-section-(template--\\d+__${escapeRegExp(shortId)})`
    );
    const match = html.match(pattern);
    const fullId = match?.[1] || '';

    if (fullId) {
      try {
        window.sessionStorage?.setItem(cacheKey, fullId);
      } catch (_error) {
        /* sessionStorage unavailable */
      }
    }

    return fullId;
  };

  const loadShowcase = async (root) => {
    if (!(root instanceof HTMLElement)) return;

    const configuredId = (root.dataset.homepageSectionId || '').trim();
    const status = root.querySelector('[data-hormonize-showcase-sync-status]');
    const mount = root.querySelector('[data-hormonize-showcase-sync-mount]');

    if (!configuredId) {
      if (status) {
        status.hidden = false;
        status.textContent = 'Homepage showcase section ID is missing.';
      }
      return;
    }

    try {
      const sectionId = await resolveSectionId(configuredId);

      if (!sectionId) {
        throw new Error('Could not resolve homepage Product Showcase section ID.');
      }

      const themeId = getThemeId();
      const payload = await fetchJson(
        buildUrl({
          sections: sectionId,
          preview_theme_id: themeId,
        })
      );

      const html = payload[sectionId];

      if (!html) {
        throw new Error('Homepage showcase section returned empty markup.');
      }

      if (!mount) return;

      mount.replaceChildren(stripAssets(html));
      if (status) status.hidden = true;
      root.dataset.loaded = 'true';
      announceReady(root);
    } catch (_error) {
      if (status) {
        status.hidden = false;
        status.textContent = window.Shopify?.designMode
          ? 'Preview sync can fail inside the theme editor. Use “View storefront” — or edit the Homepage Product Showcase.'
          : 'Unable to load the homepage Product Showcase. Please try again.';
      }
    }
  };

  const init = (root = document) => {
    const searchRoot = root instanceof Element || root instanceof Document ? root : document;
    const nodes =
      searchRoot instanceof Element && searchRoot.matches(ROOT_SELECTOR)
        ? [searchRoot]
        : Array.from(searchRoot.querySelectorAll(ROOT_SELECTOR));

    nodes.forEach((node) => {
      if (node.dataset.syncInit === 'true') return;
      node.dataset.syncInit = 'true';
      loadShowcase(node);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) init(event.target);
  });
})();
